// SkipNewEnquiryModal — quick-entry Skip enquiry form for the Collections
// surface. Captures the minimum viable enquiry (name / phone / postcode)
// plus a few high-value optionals (notes, stock type, priority) and POSTs
// to /skip/enquiries via the existing useCreateSkipEnquiry mutation.
//
// Unlike the full SkipEnquiryCreatePage it does NOT navigate to the new
// enquiry on success — it shows a brief confirmation then closes, keeping
// the user on the Collections dashboard.

import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { Input } from '../../../components/form/Input';
import { Textarea } from '../../../components/form/Textarea';
import { Select } from '../../../components/form/Select';
import { DatePicker } from '../../../components/form/DatePicker';
import { Checkbox } from '../../../components/form/Checkbox';
import { useToast } from '../../../hooks/useToast';
import { useCreateSkipEnquiry } from '../../skip/hooks/useSkipEnquiries';
import { useSkipSizes } from '../../skip/hooks/useSkipJobs';
import { useSkipJobTypes } from '../../skip/hooks/useSkipJobTypes';
import { useSkipStockTypes } from '../../skip/settings/hooks/useSkipStock';
import { StagingFileInput } from '../../../components/ui/StagingFileInput/StagingFileInput';
import { uploadEnquiryAttachmentsBackground } from '../../../api/skipAttachments';
import { CustomerSearch } from '../../bookings/components/CustomerSearch';
import {
  SitePicker,
  type SitePickerValue,
} from '../../../components/ui/SitePicker/SitePicker';
import type { CustomerSummary } from '../../../types/api';
import { DuplicateCustomerWarning } from '../../../components/ui/DuplicateCustomerWarning/DuplicateCustomerWarning';
import { useCheckCustomerDuplicates } from '../../skip/hooks/useSkipCustomers';
import styles from './SkipNewEnquiryModal.module.css';

export interface SkipNewEnquiryModalProps {
  open: boolean;
  onClose: () => void;
}

const SUCCESS_CLOSE_MS = 1500;

interface FormState {
  /** Linked customer (null when manually entered). Sent as customer_uuid. */
  customer_uuid: string | null;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_phone2: string;
  customer_postcode: string;
  /** Saved customer_sites enc_id; null when using a one-off address. */
  site_enc_id: string | null;
  customer_address_line1: string;
  customer_address_line2: string;
  customer_city: string;
  customer_county: string;
  notes: string;
  /** enc_id of the chosen stock type; used to filter size options and
   *  submitted as skip_stock_type_enc_id. */
  skip_stock_type_enc_id: string;
  skip_size_enc_id: string;
  skip_job_type_enc_id: string;
  preferred_delivery_date: string;
  is_priority: boolean;
  priority_notes: string;
}

const EMPTY_FORM: FormState = {
  customer_uuid: null,
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  customer_phone2: '',
  customer_postcode: '',
  site_enc_id: null,
  customer_address_line1: '',
  customer_address_line2: '',
  customer_city: '',
  customer_county: '',
  notes: '',
  skip_stock_type_enc_id: '',
  skip_size_enc_id: '',
  skip_job_type_enc_id: '',
  preferred_delivery_date: '',
  is_priority: false,
  priority_notes: '',
};

export function SkipNewEnquiryModal({
  open,
  onClose,
}: SkipNewEnquiryModalProps): JSX.Element {
  const toast = useToast();
  const createEnquiry = useCreateSkipEnquiry();
  const sizesQuery = useSkipSizes();
  const jobTypesQuery = useSkipJobTypes({ forSelection: true });
  const jobTypes = jobTypesQuery.data?.jobTypes ?? [];
  const stockTypesQuery = useSkipStockTypes({ forSelection: true });
  const stockTypes = stockTypesQuery.data?.stockTypes ?? [];
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [newCustomerMode, setNewCustomerMode] = useState(false);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState<CustomerSummary[]>([]);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [userChoseCreateAnyway, setUserChoseCreateAnyway] = useState(false);
  const checkDuplicates = useCheckCustomerDuplicates();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset all local state whenever the modal (re)opens.
  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setNewCustomerMode(false);
      setStagedFiles([]);
      setError(null);
      setSubmitted(false);
      setDuplicateMatches([]);
      setShowDuplicateConfirm(false);
      setUserChoseCreateAnyway(false);
    }
  }, [open]);

  // Clear any pending auto-close timer on unmount.
  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  const submitting = createEnquiry.isPending;
  const canSubmit =
    form.customer_name.trim().length > 0 &&
    form.customer_phone.trim().length > 0 &&
    form.customer_postcode.trim().length > 0 &&
    !submitting &&
    !submitted;

  function update<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  // Autofill the snapshot fields from a linked customer (mirrors
  // SkipEnquiryCreatePage.handleCustomerSelect). The name/phone/postcode
  // inputs stay editable so the snapshot can diverge from the record.
  function handleCustomerSelect(customer: CustomerSummary): void {
    setForm((prev) => ({
      ...prev,
      customer_uuid: customer.uuid,
      customer_name: customer.name ?? '',
      customer_phone: customer.phone ?? '',
      customer_postcode: customer.postcode ?? '',
      customer_email: customer.email ?? '',
      customer_phone2: customer.phone2 ?? '',
      // Reset the site selection — saved sites are scoped to the customer.
      site_enc_id: null,
      customer_address_line1: '',
      customer_address_line2: '',
      customer_city: '',
      customer_county: '',
    }));
    setNewCustomerMode(false);
  }

  function handleToggleNewCustomer(newMode: boolean): void {
    setDuplicateMatches([]);
    setUserChoseCreateAnyway(false);
    setShowDuplicateConfirm(false);
    setNewCustomerMode(newMode);
    if (newMode) {
      // Switching to manual entry drops the customer link + site so the
      // enquiry carries a fresh snapshot with no saved-site dependency.
      setForm((prev) => ({
        ...prev,
        customer_uuid: null,
        customer_email: '',
        customer_phone2: '',
        site_enc_id: null,
        customer_address_line1: '',
        customer_address_line2: '',
        customer_city: '',
        customer_county: '',
      }));
    }
  }

  async function handleCustomerBlur(): Promise<void> {
    if (!newCustomerMode || !form.customer_name.trim()) return;
    try {
      const result = await checkDuplicates.mutateAsync({
        name:     form.customer_name.trim(),
        phone:    form.customer_phone.trim() || null,
        postcode: form.customer_postcode.trim() || null,
      });
      setDuplicateMatches(result.matches);
    } catch {
      // non-fatal
    }
  }

  function handleSiteChange(value: SitePickerValue): void {
    if (value.type === 'site') {
      setForm((prev) => ({
        ...prev,
        site_enc_id: value.site_enc_id,
        customer_address_line1: '',
        customer_address_line2: '',
        customer_city: '',
        customer_county: '',
      }));
    } else if (value.type === 'one_off') {
      setForm((prev) => ({
        ...prev,
        site_enc_id: null,
        customer_address_line1: value.line1 ?? '',
        customer_address_line2: value.line2 ?? '',
        customer_city: value.city ?? '',
      }));
    } else {
      setForm((prev) => ({ ...prev, site_enc_id: null }));
    }
  }

  async function handleSubmit(): Promise<void> {
    if (!canSubmit) return;
    setError(null);

    if (newCustomerMode && duplicateMatches.length > 0 && !userChoseCreateAnyway) {
      setShowDuplicateConfirm(true);
      return;
    }

    // Sparse body — only send populated fields (matches SkipEnquiryCreatePage).
    const body: Record<string, unknown> = {
      customer_name: form.customer_name.trim(),
      customer_phone: form.customer_phone.trim(),
      customer_postcode: form.customer_postcode.trim(),
      is_priority: form.is_priority,
    };
    // Linked customer + extra snapshot fields.
    if (form.customer_uuid) body.customer_uuid = form.customer_uuid;
    if (form.customer_email.trim()) body.customer_email = form.customer_email.trim();
    if (form.customer_phone2.trim()) body.customer_phone2 = form.customer_phone2.trim();
    // Site (saved) is mutually exclusive with a one-off address.
    if (form.site_enc_id) {
      body.site_enc_id = form.site_enc_id;
    } else {
      if (form.customer_address_line1.trim()) body.customer_address_line1 = form.customer_address_line1.trim();
      if (form.customer_address_line2.trim()) body.customer_address_line2 = form.customer_address_line2.trim();
      if (form.customer_city.trim()) body.customer_city = form.customer_city.trim();
      if (form.customer_county.trim()) body.customer_county = form.customer_county.trim();
    }
    if (form.notes.trim()) body.notes = form.notes.trim();
    if (form.skip_stock_type_enc_id) body.skip_stock_type_enc_id = form.skip_stock_type_enc_id;
    if (form.skip_size_enc_id) body.skip_size_enc_id = form.skip_size_enc_id;
    if (form.skip_job_type_enc_id) body.skip_job_type_enc_id = form.skip_job_type_enc_id;
    if (form.preferred_delivery_date) body.preferred_delivery_date = form.preferred_delivery_date;
    if (form.is_priority && form.priority_notes.trim()) {
      body.priority_notes = form.priority_notes.trim();
    }

    try {
      const { enquiry } = await createEnquiry.mutateAsync(body);
      // Single-flow attachments: fire the upload in the background so the
      // confirmation + auto-close are not blocked on the upload.
      if (stagedFiles.length > 0) {
        void uploadEnquiryAttachmentsBackground(enquiry.enc_id, stagedFiles, () =>
          toast.error(
            'Enquiry created, but attachments failed to upload. Add them from the enquiry page.',
          ),
        );
      }
      setSubmitted(true);
      toast.success('Enquiry created');
      closeTimer.current = setTimeout(onClose, SUCCESS_CLOSE_MS);
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not create enquiry.'
        : 'Could not create enquiry.';
      setError(message);
    }
  }

  // Find the selected stock type's stable uuid so sizes can be filtered
  // across two separate API responses (enc_ids are non-deterministic).
  const selectedStockTypeUuid = stockTypes.find(
    (t) => t.enc_id === form.skip_stock_type_enc_id,
  )?.uuid;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Stock Enquiry"
      size="md"
      preventClose={submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {submitted ? 'Close' : 'Cancel'}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!canSubmit}
          >
            Create Enquiry
          </Button>
        </>
      }
    >
      {submitted ? (
        <p className={styles.success} role="status">
          Enquiry created successfully.
        </p>
      ) : (
        <div className={styles.body}>
          {error ? (
            <p className={styles.error} role="alert">
              {error}
            </p>
          ) : null}

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Customer</span>
            <CustomerSearch
              newCustomerMode={newCustomerMode}
              onSelect={handleCustomerSelect}
              onToggleNewCustomer={handleToggleNewCustomer}
            />
          </div>

          <Input
            label="Customer Name"
            required
            value={form.customer_name}
            onChange={(e) => update('customer_name', e.target.value)}
            onBlur={() => { void handleCustomerBlur(); }}
          />
          <div className={styles.row}>
            <Input
              label="Phone"
              required
              variant="tel"
              value={form.customer_phone}
              onChange={(e) => update('customer_phone', e.target.value)}
              onBlur={() => { void handleCustomerBlur(); }}
            />
            <Input
              label="Postcode"
              required
              value={form.customer_postcode}
              onChange={(e) => update('customer_postcode', e.target.value)}
            />
          </div>

          {form.customer_uuid ? (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Job Site</span>
              <SitePicker
                customerEncId={form.customer_uuid}
                onChange={handleSiteChange}
              />
            </div>
          ) : null}

          {newCustomerMode && (
            <>
              <Input
                label="Address"
                value={form.customer_address_line1}
                onChange={(e) => update('customer_address_line1', e.target.value)}
                placeholder="Address line 1"
              />
              <Input
                label="City"
                value={form.customer_city}
                onChange={(e) => update('customer_city', e.target.value)}
                placeholder="City"
              />
              <Input
                label="County"
                value={form.customer_county}
                onChange={(e) => update('customer_county', e.target.value)}
                placeholder="County"
              />
            </>
          )}

          {newCustomerMode && duplicateMatches.length > 0 && (
            <DuplicateCustomerWarning
              matches={duplicateMatches}
              showConfirm={showDuplicateConfirm}
              onUseExisting={(c) => {
                handleCustomerSelect(c);
                setDuplicateMatches([]);
                setUserChoseCreateAnyway(false);
              }}
              onCreateAnyway={() => {
                setUserChoseCreateAnyway(true);
                setDuplicateMatches([]);
              }}
              onDismiss={() => setDuplicateMatches([])}
              onConfirmCreate={() => {
                setUserChoseCreateAnyway(true);
                setShowDuplicateConfirm(false);
                void handleSubmit();
              }}
              onCancelConfirm={() => setShowDuplicateConfirm(false)}
            />
          )}

          <Select
            label="Stock Type"
            options={[
              {
                value: '',
                label: stockTypesQuery.isLoading ? 'Loading…' : '— Not specified —',
              },
              ...stockTypes.map((t) => ({ value: t.enc_id, label: t.name })),
            ]}
            value={form.skip_stock_type_enc_id}
            onChange={(e) => update('skip_stock_type_enc_id', e.target.value)}
            disabled={stockTypesQuery.isLoading}
          />

          <Select
            label="Stock Size"
            value={form.skip_size_enc_id}
            onChange={(e) => update('skip_size_enc_id', e.target.value)}
            options={[
              { value: '', label: '— Not specified —' },
              ...(sizesQuery.data?.skipSizes ?? [])
                .filter((s) =>
                  selectedStockTypeUuid
                    ? s.stock_type_uuid === selectedStockTypeUuid
                    : true,
                )
                .map((s) => ({ value: s.id as string, label: s.name })),
            ]}
          />

          <Select
            label="Job Type"
            value={form.skip_job_type_enc_id}
            onChange={(e) => update('skip_job_type_enc_id', e.target.value)}
            options={[
              { value: '', label: '— Not specified —' },
              ...jobTypes.map((t) => ({
                value: t.enc_id,
                label: t.process_immediately ? `${t.name} (Immediate)` : t.name,
              })),
            ]}
          />

          <DatePicker
            label="Preferred Delivery Date"
            value={form.preferred_delivery_date}
            onChange={(e) => update('preferred_delivery_date', e.target.value)}
          />

          <Textarea
            label="Notes"
            rows={3}
            value={form.notes}
            onChange={(e) => update('notes', e.target.value)}
          />

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Attachments (optional)</span>
            <StagingFileInput
              files={stagedFiles}
              onChange={setStagedFiles}
              maxFiles={20}
              disabled={submitting}
            />
          </div>

          <Checkbox
            label="Priority"
            checked={form.is_priority}
            onChange={(e) => update('is_priority', e.target.checked)}
          />
          {form.is_priority ? (
            <Input
              label="Priority reason"
              value={form.priority_notes}
              onChange={(e) => update('priority_notes', e.target.value)}
            />
          ) : null}
        </div>
      )}
    </Modal>
  );
}
